import { useNavigate } from 'react-router-dom';
import { useState, useCallback } from 'react';
import { toast } from 'react-toastify';
import { useBackend } from '../../Hooks/UseBackend';
import { Connection, Keypair, LAMPORTS_PER_SOL } from '@solana/web3.js';
import { base64ToFile } from '../../utils/base64-to-file';
import { urlToFile } from '../../utils/url-to-file';
import bs58 from 'bs58';
import { ethers, providers } from 'ethers';
import { DECIMALS, deployETHToken, SUPPLY } from '../../utils/ethereum/ERC20Token';
import { solididtyReleases } from '../../utils/types';
import { Api } from '@sodamnfoolish/sjc-backend-types';
import { getRSAEncryptedString } from '../../utils/RSA';
import { RefWrapper } from '../../Components/RefWrapper';
import { cropCenter, ImageEditor } from './ImageEditor';
import { AIImageGenerator } from './AIImageGenerator';

import * as Form from '../../Components/form';
import { SolanaIcon } from '../../Components/icons/SolanaIcon';
import { LetsBonkIcon } from '../../Components/icons/LetsBonkIcon';
import { PumpFunIcon } from '../../Components/icons/PumpFunIcon';
import { EthereumIcon } from '../../Components/icons/EthereumIcon';
import { Section } from '../../Components/Section';
import { WalletState } from '../../Components/WalletState';
import { SolanaTokenParser } from '../../Components/SolanaTokenParser';
import { TelegramChannel, TelegramSettings } from '../../telegram';
import { getSolanaWallet } from '../../utils/solana/wallet';
import { getEthereumWallet } from '../../utils/ethereum/wallet';
import Modal from '../../Components/Modal';
import { ImageSource } from './types';
import { blobToBase64 } from '../../utils/blob';
import { Tooltip } from '../../Components/Tooltip';
import { calculateRequiredMicroLamportsFromTxCost } from '../../utils/solana/calculate-required-microLamports-from-tx-cost';

const extractTextFromImage = async (imageFile: File | Blob) => {
    const formData = new FormData();
    formData.append('file', imageFile);
    formData.append('apikey', process.env.REACT_APP_OCR_API_KEY || '');

    try {
        const response = await fetch('https://api.ocr.space/parse/image', {
            method: 'POST',
            body: formData,
        });
        const result = await response.json();

        if (result.IsErroredOnProcessing) {
            console.error('Error in OCR processing:', result.ErrorMessage);
            return '';
        }

        return result.ParsedResults[0]?.ParsedText || '';
    } catch (error) {
        console.error('Error while calling OCR API:', error);
        return '';
    }
};

export function TokenCreate({
    profilePic,

    defaultName = '',
    defaultSymbol = '',
    defaultDescription = '',
    defaultImageSrc = '',
    defaultImageFile = null,
    defaultTelegram = '',
    defaultTwitter = '',
    defaultWebsite = '',

    defaultEthLiquidity,
    defaultDevBuySolana,
    defaultSnipeBuySolana,

    ethSnipeValues,

    autoSellDelay,
    autoSellProfit,

    ethPrivateKey,
    solPrivateKey,
    solSniperPrivateKey,
    ethSniperPrivateKeys,
    zeroSlotBuyTip,
    buyTxCost,

    solidityReleases,
    solConnection,
    ethProvider,

    leftColumnSlot,

    imageManipulationComponents = [],

    getAIMetadata,
    onAddDeployedSolToken,

    telegramAPIID,
    telegramAPIHash,
    telegramPhoneNumber,
}: {
    profilePic?: string;

    defaultName?: string;
    defaultSymbol?: string;
    defaultDescription?: string;
    defaultImageSrc?: string;
    defaultImageFile?: File | null;
    defaultTelegram?: string;
    defaultTwitter?: string;
    defaultWebsite?: string;

    defaultEthLiquidity: number;
    defaultDevBuySolana: number;
    defaultSnipeBuySolana: number;

    ethSnipeValues: number[];

    autoSellDelay: number;
    autoSellProfit: number;

    ethPrivateKey: string;
    solPrivateKey: string;
    solSniperPrivateKey: string;
    ethSniperPrivateKeys: string[];
    zeroSlotBuyTip: number;
    buyTxCost: number;

    solidityReleases: solididtyReleases;
    solConnection: Connection;
    ethProvider: providers.JsonRpcProvider;

    leftColumnSlot?: React.ReactNode;

    imageManipulationComponents?: React.ComponentType<ImageSource>[];

    getAIMetadata?: (() => Promise<Api.AI.DTO.Metadata>) | null;

    onAddDeployedSolToken: (address: string, name: string, symbol: string, image: string) => void;
} & TelegramSettings) {
    const navigate = useNavigate();

    const { deployWithSnipe, uploadMetadata } = useBackend();

    const [imageSrc, setImageSrc] = useState(defaultImageSrc);
    const [imageFile, setImageFile] = useState(defaultImageFile);

    const [mintSecretKeyBase58, setMintSecretKeyBase58] = useState<string>('');

    const [name, setName] = useState(defaultName);
    const [symbol, setSymbol] = useState(defaultSymbol);
    const [description, setDescription] = useState(defaultDescription);

    const [telegram, setTelegram] = useState(defaultTelegram);
    const [twitter, setTwitter] = useState(defaultTwitter);
    const [website, setWebsite] = useState(defaultWebsite);

    const [symbolAutocapChecked, setSymbolAutocapChecked] = useState(true);

    const [ethLiquidity, setEthLiquidity] = useState<number>(defaultEthLiquidity);
    const [devBuySolana, setDevBuySolana] = useState<number>(defaultDevBuySolana);
    const [snipeBuySolana, setSnipeBuySolana] = useState<number>(defaultSnipeBuySolana);

    const [ethAutoOpen, setEthAutoOpen] = useState<boolean>(true);
    const [snipeEthChecked, setSnipeEthChecked] = useState(true);
    const [snipeEthWalletsNumber, setSnipeEthWalletsNumber] = useState<number>(ethSniperPrivateKeys.length);

    const [dex, setDex] = useState<'pump' | 'bonk'>('bonk');
    const [isSuffix, setIsSuffix] = useState<boolean>(true);

    const [snipeBuyChecked, setSnipeBuyChecked] = useState<boolean>(true);

    const getImageFromUrl = useCallback(async (url: string) => {
        setImageSrc(url);
        const file = await urlToFile(url, 'metadataImage.jpg', 'image/jpeg');
        setImageFile(file);
        setIsOpen(false);
    }, []);

    const imageFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file) {
            blobToBase64(file)
                .then((base64Image) => {
                    setImageSrc(base64Image);
                    setImageFile(file);
                })
                .catch((error) => {
                    console.error('Error converting file to Base64:', error);
                });
        }
    };

    const deployTokenFromMetadata = async (
        name: string,
        symbol: string,
        imageFile: File | null,
        solPrivateKey: string,
        dex: 'pump' | 'bonk',
        mintSecretKeyBase58: string | undefined,
        buyAmount: number,
        solSniperPrivateKey?: string,
        snipeSolValue?: number,
    ) => {
        if (!imageFile) return toast.error('no image file');
        if (!solPrivateKey) return toast.error('no wallet address');
        if (mintSecretKeyBase58)
            try {
                Keypair.fromSecretKey(new Uint8Array(bs58.decode(mintSecretKeyBase58)));
            } catch (error) {
                return toast.error('Invalid mint secret key');
            }

        toast.info('Uploading metadata...');
        const uploadMetadataResponse = await uploadMetadata({
            file: imageFile,
            name: name,
            symbol: symbol,
            description: description,
            twitter: twitter,
            telegram: telegram,
            website: website,
            showName: 'true',
        });
        const requestData: Api.Wallet.DTO.DeployWithSniperRequest = {
            name: name,
            symbol: symbol,
            metadataUri: uploadMetadataResponse.metadataUri,
            deploy: {
                encodedPrivateKey: getRSAEncryptedString(solPrivateKey),
                priorityFee: Number(calculateRequiredMicroLamportsFromTxCost(buyTxCost, 150_000)),
                zeroSlotTip: zeroSlotBuyTip * LAMPORTS_PER_SOL,
                buyAmount: buyAmount,
            },
            snipers:
                solSniperPrivateKey && snipeSolValue
                    ? [
                          {
                              encodedPrivateKey: getRSAEncryptedString(solSniperPrivateKey),
                              priorityFee: Number(calculateRequiredMicroLamportsFromTxCost(buyTxCost, 150_000)),
                              zeroSlotTip: zeroSlotBuyTip * LAMPORTS_PER_SOL,
                              amount: snipeSolValue,
                          },
                      ]
                    : [],
            mintSecretKeyBase58: mintSecretKeyBase58,
            commitment: 'processed',
            platform: dex,
        };
        console.log('Deploy request data:', requestData);

        const result = await deployWithSnipe(requestData);

        if (!result) {
            toast.error('Deploy failed');
            return;
        }

        const { mint, deploySignature, snipeSignatures } = result;
        toast.success(`Deploy transaction sent: ${deploySignature}.`);
        console.log('Deploy transaction sent:', deploySignature);
        snipeSignatures.forEach((signature) => {
            toast.success(`Snipe transaction sent: ${signature}.`);
        });
        console.log('Snipe transactions sent:', snipeSignatures);
        onAddDeployedSolToken(mint, name, symbol, uploadMetadataResponse.metadata.image);
        navigate(`/Result`, {
            state: {
                initBuyDeployer: buyAmount / LAMPORTS_PER_SOL,
                initBuySniper: snipeSolValue ? snipeSolValue / LAMPORTS_PER_SOL : 0,
                autoSellProfit: autoSellProfit,
                autoSellDelay: autoSellDelay,
            },
        });
    };

    const handleExtractText = async () => {
        if (!imageFile) {
            toast.error('Upload image first');
            return;
        }

        const extractedText = await extractTextFromImage(imageFile);
        if (extractedText) {
            setDescription((prevDescription) => `${extractedText}\n${prevDescription}`);
            toast.success('Text extract successfully!');
        } else {
            toast.error('Image dont have text');
        }
    };

    const [isOpen, setIsOpen] = useState(false);

    const handleOutsideClick = (e: React.MouseEvent<HTMLDivElement>) => {
        if (e.target === e.currentTarget) {
            setIsOpen(false);
        }
    };

    return (
        <div className='flex text-light-grey'>
            <div className='w-[284px] border border-midnight p-[12px]'>
                {leftColumnSlot ? (
                    leftColumnSlot
                ) : (
                    <SolanaTokenParser
                        onMetadataChange={(metadata) => {
                            if (!metadata) return;

                            setName(metadata.name ?? '');
                            setSymbol(metadata.symbol ?? '');
                            setDescription(metadata.description ?? '');
                            setTelegram(metadata.telegram ?? '');
                            setTwitter(metadata.twitter ?? '');
                            setWebsite(metadata.website ?? '');

                            getImageFromUrl(metadata.image ?? '');
                        }}
                    />
                )}
            </div>
            <div className='w-[670px] flex flex-col p-[12px] gap-[20px]'>
                <div className='flex flex-col items-center'>
                    <form className='flex flex-col gap-[9px]'>
                        <label className='flex items-center'>
                            <span className='w-[130px] text-right mr-[12px]'>Name</span>
                            <Form.Input
                                className='h-[34px] w-[256px]'
                                value={name}
                                onChange={(event) => {
                                    setName(event.target.value);
                                }}
                            />
                        </label>
                        <div className='flex items-center'>
                            <label className='flex items-center mr-[9px]'>
                                <span className='w-[130px] text-right mr-[12px]'>Symbol</span>
                                <Form.Input
                                    className='h-[34px] w-[154px]'
                                    value={symbol}
                                    onChange={(event) => {
                                        let text = event.target.value;
                                        text = symbolAutocapChecked ? text.toUpperCase() : text;

                                        function trimTextToFitLimits(text: string) {
                                            const specialCharRegex = /[^a-zA-Z0-9]/g;
                                            const regularCharRegex = /[a-zA-Z0-9]/g;

                                            const specialChars = text.match(specialCharRegex) || [];
                                            const regularChars = text.match(regularCharRegex) || [];

                                            let result = '';

                                            if (specialChars.length > 2 || (specialChars.length === 1 && regularChars.length > 6) || regularChars.length > 10) {
                                                if (specialChars.length > 2) {
                                                    result += specialChars.slice(0, 2).join('');
                                                } else if (specialChars.length === 1 && regularChars.length > 6) {
                                                    result += specialChars.join('') + regularChars.slice(0, 6).join('');
                                                } else if (regularChars.length > 10) {
                                                    result += regularChars.slice(0, 10).join('');
                                                }
                                            } else {
                                                result = text;
                                            }

                                            return result;
                                        }

                                        text = trimTextToFitLimits(text);
                                        setSymbol(text);
                                    }}
                                />
                            </label>
                            <label className='flex items-center'>
                                <Form.Checkbox
                                    className='size-[20px] mr-[9px]'
                                    checked={symbolAutocapChecked}
                                    onChange={() => {
                                        if (!symbolAutocapChecked) {
                                            setSymbol(symbol.toUpperCase());
                                        }
                                        setSymbolAutocapChecked(!symbolAutocapChecked);
                                    }}
                                />
                                <span className='text-[10px]'>Auto Cap</span>
                            </label>
                        </div>
                        <label className='flex items-center'>
                            <span className='w-[130px] text-right mr-[12px]'>Description</span>
                            <Form.Textarea
                                value={description}
                                onChange={(event) => {
                                    setDescription(event.target.value);
                                }}
                                className='w-[327px] h-[98px]'
                            />
                        </label>
                    </form>
                </div>
                <ImageEditor
                    imageSrc={imageSrc}
                    onImageChange={(dataUrl, file) => {
                        setImageSrc(dataUrl);
                        setImageFile(file);
                    }}
                >
                    {(cropImage, openImageEditor) => (
                        <form className='flex flex-col items-center border border-midnight p-[14px] gap-[26px]'>
                            <div className='flex gap-[30px]'>
                                <div className='w-[90px] flex flex-wrap gap-[9px] self-start'>
                                    <Tooltip text='Remove Image'>
                                        <Form.Button
                                            className='!size-[40px] bg-cover bg-transparent bg-[image:var(--delete-svg)]'
                                            onClick={() => {
                                                setImageSrc('');
                                                setImageFile(null);
                                            }}
                                        />
                                    </Tooltip>

                                    <Tooltip text='Open Image Editor'>
                                        <Form.Button
                                            className='!size-[40px] bg-cover bg-transparent bg-[image:var(--image-editor-svg)]'
                                            onClick={openImageEditor}
                                        />
                                    </Tooltip>

                                    <Tooltip text='Upload Image'>
                                        <Form.Input
                                            type='file'
                                            className='relative size-[40px] !p-0 !bg-transparent'
                                            icon={
                                                <div className='absolute top-0 left-0 pointer-events-none size-[40px] bg bg-[image:var(--upload-svg)] cursor-pointer' />
                                            }
                                            inputClassName='opacity-0'
                                            accept='image/*'
                                            onChange={imageFileChange}
                                        />
                                    </Tooltip>

                                    <Tooltip text='Import Image From URL'>
                                        <Form.Button
                                            className='!size-[40px] bg-cover bg-transparent bg-[image:var(--import-svg)]'
                                            onClick={() => {
                                                setIsOpen(true);
                                            }}
                                        />
                                    </Tooltip>
                                    {imageManipulationComponents.map((ImageManipulation) => (
                                        <ImageManipulation
                                            onImageChange={(dataUrl, file) => {
                                                setImageSrc(dataUrl);
                                                setImageFile(file);
                                            }}
                                        />
                                    ))}

                                    {
                                        <Modal
                                            header={<span>Import Image From Web</span>}
                                            open={isOpen}
                                            onClose={() => setIsOpen(false)}
                                            onClick={handleOutsideClick}
                                        >
                                            <RefWrapper<HTMLInputElement>>
                                                {(ref) => (
                                                    <div className='flex'>
                                                        <Form.Input ref={ref} placeholder='Enter image URL' />
                                                        <Form.Button
                                                            className=''
                                                            onClick={() => {
                                                                if (ref.current?.value) {
                                                                    getImageFromUrl(ref.current.value);
                                                                }
                                                            }}
                                                        >
                                                            Import
                                                        </Form.Button>
                                                    </div>
                                                )}
                                            </RefWrapper>
                                        </Modal>
                                    }
                                </div>
                                <div className='flex items-center justify-center'>
                                    {imageSrc ? <img className='size-[183px]' src={imageSrc ?? ''} alt='Uploaded' /> : <span>image from post</span>}
                                </div>
                                <div className='flex flex-col gap-[9px]'>
                                    <Form.Button
                                        className='w-[103px] h-[34px] text-[14px] font-bold whitespace-nowrap'
                                        onClick={() => {
                                            cropImage(({ height, width }) => {
                                                let diff = height - width;

                                                if (diff > 0) {
                                                    return { left: 0, top: 0, width, height: height - diff };
                                                } else {
                                                    return cropCenter({ height, width });
                                                }
                                            });
                                        }}
                                    >
                                        Crop Upper
                                    </Form.Button>
                                    <Form.Button
                                        className='w-[103px] h-[34px] text-[14px] font-bold whitespace-nowrap'
                                        onClick={() => {
                                            cropImage(cropCenter);
                                        }}
                                    >
                                        Crop Center
                                    </Form.Button>
                                    <Form.Button
                                        className='w-[103px] h-[34px] text-[14px] font-bold whitespace-nowrap'
                                        onClick={() => {
                                            if (profilePic) {
                                                setImageSrc(`data:image/jpeg;base64,${profilePic}`);
                                                const file = base64ToFile(`data:image/jpeg;base64,${profilePic}`, 'profilePic.jpg');
                                                setImageFile(file);
                                            } else {
                                                console.error('No image available in source details');
                                            }
                                        }}
                                    >
                                        Profle Pic
                                    </Form.Button>
                                </div>
                            </div>
                            <div className='flex items-center gap-[24px]'>
                                <span>AI image</span>
                                <AIImageGenerator
                                    onImageChange={(dataUrl, file) => {
                                        setImageSrc(dataUrl);
                                        setImageFile(file);
                                    }}
                                />
                            </div>
                            {getAIMetadata && (
                                <div className='flex justify-center  gap-[24px]'>
                                    <Form.Button
                                        className='h-[34px] text-[14px] font-bold'
                                        onClick={async () => {
                                            const metadata = await getAIMetadata();
                                            setName(metadata.name);
                                            setSymbol(metadata.symbol);
                                            setDescription(twitter);
                                        }}
                                    >
                                        Generate with AI
                                    </Form.Button>
                                    <Form.Button className='h-[34px] text-[14px] font-bold' onClick={handleExtractText}>
                                        Text extract image
                                    </Form.Button>
                                </div>
                            )}
                        </form>
                    )}
                </ImageEditor>
                <div className='flex gap-[80px] justify-center'>
                    <form className='flex flex-col items-end gap-[9px]'>
                        <label className='flex items-center'>
                            <span className='text-right mr-[12px]'>Telegram</span>
                            <Form.Input
                                className='h-[34px]'
                                value={telegram}
                                onChange={(event) => {
                                    setTelegram(event.target.value);
                                }}
                            />
                        </label>
                        <label className='flex items-center'>
                            <span className='text-right mr-[12px]'>Twitter</span>
                            <Form.Input
                                value={twitter}
                                onChange={(event) => {
                                    setTwitter(event.target.value);
                                }}
                                className='h-[34px]'
                            />
                        </label>
                        <label className='flex items-center'>
                            <span className='text-right mr-[12px]'>Website</span>
                            <Form.Input
                                value={website}
                                onChange={(event) => {
                                    setWebsite(event.target.value);
                                }}
                                className='h-[34px]'
                            />
                        </label>
                    </form>
                    <TelegramChannel
                        tokenName={name}
                        telegramAPIID={telegramAPIID}
                        telegramAPIHash={telegramAPIHash}
                        telegramPhoneNumber={telegramPhoneNumber}
                    />
                </div>
            </div>
            <div className='min-w-[297px] border border-midnight'>
                <DeploySection
                    bodyClassName='flex flex-col w-full p-[8px] text-[12px] gap-[15px]'
                    icon={<SolanaIcon className='size-[20px]' />}
                    currency={'SOL'}
                    getWalletStatus={() => getSolanaWallet(solPrivateKey, solConnection)}
                >
                    <div className='flex gap-[10px]'>
                        <label className='flex'>
                            <Form.Radio
                                name='dex'
                                value='bonk'
                                className='mr-[7px]'
                                checked={dex === 'bonk'}
                                onChange={(event) => {
                                    setDex(event.target.value as typeof dex);
                                }}
                            />
                            <span>LetsBonk</span>
                        </label>
                        <label className='flex'>
                            <Form.Radio
                                name='dex'
                                value='pump'
                                className='mr-[7px]'
                                checked={dex === 'pump'}
                                onChange={(event) => {
                                    setDex(event.target.value as typeof dex);
                                }}
                            />
                            <span>PumpFun</span>
                        </label>
                    </div>
                    <div className='flex gap-[10px] items-center'>
                        <label className='flex'>
                            <Form.Radio
                                name='wallet'
                                className='mr-[7px]'
                                checked={isSuffix}
                                onChange={() => {
                                    setIsSuffix(true);
                                    setMintSecretKeyBase58('');
                                }}
                            />
                            <span>{dex === 'bonk' ? 'Bonk' : 'Pump'}</span>
                        </label>
                        <label className='flex'>
                            <Form.Radio
                                name='wallet'
                                className='mr-[7px]'
                                checked={!isSuffix}
                                onChange={() => {
                                    setIsSuffix(false);
                                }}
                            />
                            <span>Custom</span>
                        </label>
                        <Form.Input
                            value={mintSecretKeyBase58}
                            onChange={(e) => setMintSecretKeyBase58(e.target.value)}
                            disabled={isSuffix}
                            className='h-[23px] w-[135px]'
                        />
                    </div>
                    <div className='flex items-center pl-[8px]'>
                        <Form.Button
                            className={`w-[126px] h-[46px] flex items-center mr-[24px] ${
                                dex === 'bonk'
                                    ? 'bg-[linear-gradient(to_right,var(--color-mango),var(--color-orange))]'
                                    : 'bg-[linear-gradient(to_right,var(--color-mint-green),var(--color-fresh-green))]'
                            }`}
                            onClick={() => {
                                deployTokenFromMetadata(
                                    name,
                                    symbol,
                                    imageFile,
                                    solPrivateKey,
                                    dex,
                                    isSuffix ? undefined : mintSecretKeyBase58,
                                    Math.floor(devBuySolana * LAMPORTS_PER_SOL),
                                    snipeBuyChecked ? solSniperPrivateKey : undefined,
                                    snipeBuyChecked ? Math.floor(snipeBuySolana * LAMPORTS_PER_SOL) : undefined,
                                );
                            }}
                        >
                            {dex === 'bonk' ? <LetsBonkIcon className='mr-[6px]' /> : <PumpFunIcon className='mr-[6px]' />}
                            <span className='text-[18px] text-dark-night font-extrabold'>DEPLOY</span>
                        </Form.Button>
                        <label className='flex justify-end items-center'>
                            <span className='mr-[12px] text-right'>DevBuy</span>
                            <Form.Float className='mr-[8px]' value={devBuySolana} onChange={(e) => setDevBuySolana(e.target.value)} />
                            <span>SOL</span>
                        </label>
                    </div>
                    <div className='flex items-center pl-[8px]'>
                        <label className='w-[126px] flex justify-end items-center mr-[33px] '>
                            <span className='mr-[12px] text-right'>Snipe with 2nd Wallet</span>
                            <Form.Checkbox className='size-[20px]' checked={snipeBuyChecked} onChange={(e) => setSnipeBuyChecked(e.target.checked)} />
                        </label>
                        <label className='flex justify-end items-center'>
                            <span className='mr-[12px] text-right'>Snipe</span>
                            <Form.Float className='mr-[8px]' value={snipeBuySolana} onChange={(e) => setSnipeBuySolana(Number(e.target.value))} />
                            <span>SOL</span>
                        </label>
                    </div>
                </DeploySection>
                <DeploySection
                    bodyClassName='flex flex-col w-full p-[8px] text-[12px] gap-[15px]'
                    icon={<EthereumIcon className='size-[20px] filter invert' />}
                    currency={'ETH'}
                    getWalletStatus={() => getEthereumWallet(ethPrivateKey, ethProvider)}
                >
                    <div className='flex gap-[10px] items-center'>
                        <label className='flex'>
                            <Form.Radio checked={true} disabled={true} className='mr-[7px]' />
                            <span>ReduceTaxCA</span>
                        </label>
                        <label className='flex'>
                            <Form.Radio checked={false} disabled={false} className='mr-[7px]' />
                            <span>2nd Option</span>
                        </label>
                    </div>
                    <div className='flex items-center pl-[8px] pr-[12px] gap-[20px]'>
                        <Form.Button
                            onClick={async () => {
                                const [address, contractBody] = await deployETHToken(
                                    ethLiquidity.toString(),
                                    description,
                                    name,
                                    symbol,
                                    ethPrivateKey,
                                    ethProvider,
                                    solidityReleases,
                                    '98',
                                    snipeEthChecked
                                        ? {
                                              sniperPrivateKeys: ethSniperPrivateKeys,
                                              sniperBuyTokenAmounts: ethSnipeValues.map((sniperBuyPercentage) => {
                                                  return ethers.utils.parseUnits(((SUPPLY * Number(sniperBuyPercentage)) / 100).toString(), DECIMALS);
                                              }),
                                          }
                                        : undefined,
                                );
                                navigate(`/ErcTokenCreate`, {
                                    state: {
                                        initLiquidity: ethLiquidity,
                                        description: description,
                                        name: name,
                                        symbol: symbol,
                                        address: address,
                                        contractBody: contractBody,
                                        autoOpen: ethAutoOpen,
                                    },
                                });
                            }}
                            className='min-w-[126px] h-[46px] flex items-center bg-[linear-gradient(to_right,var(--color-light-cyan),var(--color-bright-cyan))]'
                        >
                            <EthereumIcon className='mr-[6px]' />
                            <span className='text-[18px] text-dark-night font-extrabold'>DEPLOY</span>
                        </Form.Button>
                        <div className='shrink min-w-0 flex flex-col gap-[10px]'>
                            <label className='flex justify-end items-center'>
                                <span className='mr-[7px] text-right'>Liquidity</span>
                                <Form.Float className='h-[31px] mr-[7px]' value={ethLiquidity} onChange={(e) => setEthLiquidity(Number(e.target.value))} />
                                <span>ETH</span>
                            </label>
                            <label className='flex items-center'>
                                <span className='text-[12px] text-right mr-[7px]'>Auto Open</span>
                                <Form.Checkbox checked={ethAutoOpen} onChange={(e) => setEthAutoOpen(e.target.checked)} className='size-[20px] mr-[9px]' />
                            </label>
                        </div>
                    </div>
                    <div className='flex pl-[8px] '>
                        <label className='flex items-center'>
                            <span className='text-[12px] text-right mr-[7px]'>Snipe Wallets</span>
                            <Form.Checkbox checked={snipeEthChecked} onChange={(e) => setSnipeEthChecked(e.target.checked)} className='size-[20px] mr-[9px]' />
                        </label>
                        <label className='flex justify-end items-center'>
                            <span className='mr-[7px] text-right'># of Wallets</span>
                            <Form.Input
                                type='number'
                                value={snipeEthWalletsNumber}
                                onChange={(e) => {
                                    const value = Number(e.target.value);
                                    setSnipeEthWalletsNumber(Math.max(0, Math.min(value, ethSniperPrivateKeys.length)));
                                }}
                                disabled={!snipeEthChecked}
                                className='w-[53px] h-[31px] mr-[7px]'
                            />
                        </label>
                    </div>
                </DeploySection>
            </div>
        </div>
    );
}

function DeploySection({
    bodyClassName = '',

    icon,
    currency,
    getWalletStatus,
    children,
}: React.PropsWithChildren<{
    bodyClassName?: string;
    icon: React.ReactNode;
    currency: string;
    getWalletStatus: () => Promise<{ address: string; balance: number }>;
}>) {
    return (
        <Section icon={icon} title={null} headerSlot={<WalletState currency={currency} getWalletStatus={getWalletStatus} />} bodyClassName={bodyClassName}>
            {children}
        </Section>
    );
}
